import * as React from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, useWatch, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { removeEmptyFields } from '@/lib/utils';
import { readFormDraft, useFormDraft } from '@/lib/hooks';
import { contractSchema, type ContractFormValues } from '../schemas';
import type { CreateContractDto } from '../types';
import { useContract, useContractMutations, useCurrencyRate } from '../hooks';

const BILLING_PERIOD_TYPE_MAP = {
  WEEKLY: 'WEEK',
  MONTHLY: 'MONTH',
  QUARTER: 'QUARTER',
  SEMI_ANNUAL: 'HALF_YEAR',
  YEARLY: 'YEAR',
  INSTANCE: 'INSTANCE',
  MANUAL: 'MANUAL',
} as const;

// Reverse of BILLING_PERIOD_TYPE_MAP — maps the API's stored `periodType`
// back to the form's `billingPeriodType` so editing a contract preserves the
// selected cycle (otherwise the HOURLY validation reports it as missing).
const API_TO_BILLING_PERIOD_TYPE = {
  WEEK: 'WEEKLY',
  MONTH: 'MONTHLY',
  QUARTER: 'QUARTER',
  HALF_YEAR: 'SEMI_ANNUAL',
  YEAR: 'YEARLY',
  INSTANCE: 'INSTANCE',
  MANUAL: 'MANUAL',
} as const;

function toLocalDateStart(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}T00:00:00.000Z`;
}

function toLocalDateEnd(d: Date): string {
  // Build end-of-day in the user's LOCAL timezone, then serialize via
  // toISOString so the resulting UTC instant correctly represents
  // "23:59:59.999 of this local day". The old `${y}-${m}-${day}T23:59:59.999Z`
  // literal hard-coded UTC time, so in any positive-offset TZ the value
  // landed on the NEXT local day (e.g. Mar 31 23:59 UTC = Apr 1 04:59 local
  // in Tashkent), making endDate asymmetric to startDate.
  return new Date(
    d.getFullYear(),
    d.getMonth(),
    d.getDate(),
    23,
    59,
    59,
    999,
  ).toISOString();
}

// Helper to convert string/number to number or undefined
function toNumber(val: string | number | null | undefined): number | undefined {
  if (val == null || val === '') return undefined;
  const num = typeof val === 'string' ? parseFloat(val) : val;
  return isNaN(num) ? undefined : num;
}

// Contract name format enforced by the backend: {Text}-{Number}-{Number}.
const CONTRACT_NAME_PATTERN = /^[a-zA-Z]+-\d+-\d+$/;

// Create-form draft persisted to localStorage so a refresh / accidental exit
// doesn't lose the user's input. Create mode only — never applied when editing.
const CONTRACT_DRAFT_KEY = 'contract-create-draft';

// Draft values come back from JSON with Date fields serialized as ISO strings —
// revive them so the date pickers and the schema (z.date on billing periods)
// receive real Date instances.
function reviveDraftDates(values: Partial<ContractFormValues>): Partial<ContractFormValues> {
  const toDate = (v: unknown): Date | undefined =>
    v ? new Date(v as string | number | Date) : undefined;
  return {
    ...values,
    startsAt: toDate(values?.startsAt) as Date,
    endsAt: toDate(values?.endsAt),
    billingPeriods: (values?.billingPeriods ?? []).map((bp) => ({
      startsAt: toDate(bp?.startsAt) ?? null,
      endsAt: toDate(bp?.endsAt) ?? null,
    })),
  };
}

export function useContractForm() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const clientIdFromUrl = searchParams.get('clientId');
  const navigate = useNavigate();
  const isEditMode = !!id;

  const { data: existingContract, isLoading: isLoadingContract } = useContract(id);
  const { data: currencyRateData } = useCurrencyRate();
  const { createMutation, updateMutation } = useContractMutations();

  const [contractStatus, setContractStatus] = React.useState<string>('NEW');
  const [isFormReady, setIsFormReady] = React.useState(!isEditMode);

  // Compute form values from existingContract
  const formValues = React.useMemo(() => {
    if (!existingContract) {
      return {
        name: '',
        description: '',
        contactFace: '',
        contactPhone: '',
        contactEmail: '',
        vidRabot: '',
        sudOrgan: '',
        type: 'HOURLY' as const,
        reason: 'USTAV' as const,
        currency: 'USD' as const,
        dolarRate: undefined,
        price: undefined,
        advancePayment: undefined,
        advancePaymentUnit: 'usd' as const,
        claimedPrice: undefined,
        hourlyPackageId: undefined,
        periodId: undefined,
        minAdvanceForHourly: undefined,
        clientId: clientIdFromUrl ? Number(clientIdFromUrl) : undefined as unknown as number,
        practiceAreaId: undefined as unknown as number,
        startsAt: undefined as unknown as Date,
        endsAt: undefined,
        successPackage: {
          advancePayment: undefined,
          successPayment: undefined,
        },
        hourlyPackage: {
          hours: undefined,
          price: undefined,
          overTimeHourlyCost: undefined,
          rates: [] as { roleId: number; rate?: number }[],
        },
        billingPeriodType: undefined,
        billingPeriodCount: undefined,
        billingPeriods: [],
      };
    }

    return {
      name: existingContract.name ?? '',
      description: existingContract.description ?? '',
      contactFace: existingContract.contactFace ?? '',
      contactPhone: existingContract.contactPhone ?? '',
      contactEmail: existingContract.contactEmail ?? '',
      vidRabot: existingContract.vidRabot ?? '',
      sudOrgan: existingContract.sudOrgan ?? '',
      type: existingContract.type || 'HOURLY',
      reason: existingContract.reason ?? 'USTAV',
      currency: existingContract.currency ?? 'USD',
      dolarRate: toNumber(existingContract.dolarRate),
      price: toNumber(existingContract.price),
      advancePayment: toNumber(existingContract.advancePayment),
      advancePaymentUnit: existingContract.advancePaymentUnit ?? 'usd',
      claimedPrice: toNumber(existingContract.claimedPrice),
      hourlyPackageId: existingContract.hourlyPackageId,
      periodId: existingContract.periodId,
      minAdvanceForHourly: toNumber(existingContract.hourlyPackage?.price),
      clientId: existingContract.clientId,
      practiceAreaId: existingContract.practiceAreaId,
      startsAt: existingContract.startsAt ? new Date(existingContract.startsAt) : undefined,
      endsAt: existingContract.endsAt ? new Date(existingContract.endsAt) : undefined,
      successPackage: existingContract.successPackage
        ? {
            advancePayment: toNumber(existingContract.successPackage.advancePayment),
            successPayment: toNumber(existingContract.successPackage.successPayment),
          }
        : {
            advancePayment: undefined,
            successPayment: undefined,
          },
      hourlyPackage: existingContract.hourlyPackage
        ? {
            hours: toNumber(existingContract.hourlyPackage.hours),
            price: toNumber(existingContract.hourlyPackage.price),
            overTimeHourlyCost: toNumber(existingContract.hourlyPackage.overTimeHourlyCost),
            rates: existingContract.hourlyPackage.rates?.map(r => ({
              roleId: r.roleId,
              rate: toNumber(r.rate),
            })) ?? [],
          }
        : {
            hours: undefined,
            price: undefined,
            overTimeHourlyCost: undefined,
            rates: [] as { roleId: number; rate?: number }[],
          },
      billingPeriodType: existingContract.periodType
        ? API_TO_BILLING_PERIOD_TYPE[existingContract.periodType]
        : undefined,
      billingPeriodCount: undefined,
      billingPeriods: (() => {
        const sorted = [...(existingContract.billingPeriods ?? [])].sort(
          (a, b) =>
            (a.endDate ? new Date(a.endDate).getTime() : 0) -
            (b.endDate ? new Date(b.endDate).getTime() : 0),
        );
        return sorted.map((bp, i) => {
          // The first period shows its real stored start (the contract start).
          // For every later period, ignore the stored start — it's the previous
          // period's 23:59:59.999 end, which renders on the prior calendar day
          // (e.g. Mar 31) — and display previous-end + 1ms, i.e. 00:00:00.000 of
          // the next day (Apr 1), so the picker shows the true period start.
          // Submit sends only endDate, so this display-only start never reaches
          // the backend.
          const prevEnd = i > 0 ? sorted[i - 1].endDate : null;
          return {
            startsAt:
              i === 0
                ? bp.startDate
                  ? new Date(bp.startDate)
                  : null
                : prevEnd
                  ? new Date(new Date(prevEnd).getTime() + 1)
                  : null,
            endsAt: bp.endDate ? new Date(bp.endDate) : null,
          };
        });
      })(),
    };
  }, [existingContract, clientIdFromUrl]);

  // Restore an unfinished create-form draft (read once, before the first
  // render, so it lands as defaultValues — this keeps the type/currency
  // "reset on change" effects from seeing the restore as a user change and
  // wiping the drafted fields).
  const [draft] = React.useState(() => {
    if (isEditMode) return null;
    const stored = readFormDraft<Partial<ContractFormValues>>(CONTRACT_DRAFT_KEY);
    if (!stored) return null;
    // Never restore the template picker's id: the combobox re-resolves a
    // selectedId by fetching the entity and firing onSelectItem, and the
    // template handler overwrites hourlyPackage.* (+ advance fields) with the
    // template's values — clobbering the drafted, possibly user-customized
    // tariff. The template is only a prefill; the real values are in
    // `hourlyPackage`, which restores on its own.
    const { hourlyPackageId: _template, ...values } = stored.values;
    return { values: reviveDraftDates(values) };
  });

  const form = useForm<ContractFormValues>({
    resolver: zodResolver(contractSchema),
    defaultValues: (draft
      ? {
          ...formValues,
          ...draft.values,
          // An explicit ?clientId= link always wins over a stale draft.
          clientId: clientIdFromUrl ? Number(clientIdFromUrl) : draft.values?.clientId,
        }
      : formValues) as ContractFormValues,
  });

  const { clearDraft } = useFormDraft({
    key: CONTRACT_DRAFT_KEY,
    form,
    enabled: !isEditMode,
  });

  const watchedType = useWatch({ control: form.control, name: 'type' });
  const selectedCurrency = useWatch({ control: form.control, name: 'currency' });

  const selectedType = watchedType ?? form.getValues('type') ?? 'HOURLY';

  // Watch fields for section completion indicators
  const watchedFields = useWatch({ control: form.control });

  const isBasicInfoComplete = React.useMemo(() => {
    return !!(
      watchedFields.type &&
      watchedFields.practiceAreaId &&
      watchedFields.clientId &&
      watchedFields.startsAt
    );
  }, [watchedFields.type, watchedFields.practiceAreaId, watchedFields.clientId, watchedFields.startsAt]);

  const isPricingComplete = React.useMemo(() => {
    if (selectedType === 'PRO_BONO') return true;
    if (selectedType === 'FIXED') return !!watchedFields.price;
    if (selectedType === 'SUCCESS_FEE') return !!watchedFields.claimedPrice;
    if (selectedType === 'HOURLY') {
      return !!(
        watchedFields.hourlyPackage?.hours &&
        watchedFields.hourlyPackage?.price &&
        watchedFields.hourlyPackage?.overTimeHourlyCost
      );
    }
    return false;
  }, [
    selectedType,
    watchedFields.price,
    watchedFields.claimedPrice,
    watchedFields.hourlyPackage?.hours,
    watchedFields.hourlyPackage?.price,
    watchedFields.hourlyPackage?.overTimeHourlyCost,
  ]);

  const isFinancialComplete = React.useMemo(() => {
    if (selectedType === 'PRO_BONO') return true;
    return !!watchedFields.currency;
  }, [selectedType, watchedFields.currency]);

  const isBillingPeriodsComplete = React.useMemo(() => {
    if (selectedType !== 'HOURLY') return false;
    if (!watchedFields.billingPeriodType) return false;
    // INSTANCE periods are started/stopped manually after creation — the
    // single generated placeholder may be open-ended (no end date).
    if (watchedFields.billingPeriodType === 'INSTANCE') return true;
    const periods = watchedFields.billingPeriods;
    if (!periods || periods.length === 0) return false;
    return periods.every((bp) => bp?.startsAt && bp?.endsAt);
  }, [selectedType, watchedFields.billingPeriodType, watchedFields.billingPeriods]);

  // Load currency rate on mount
  React.useEffect(() => {
    if (currencyRateData?.rate && !isEditMode) {
      form.setValue('dolarRate', currencyRateData.rate);
    }
  }, [currencyRateData, isEditMode, form]);

  // Hydrate the form from the server ONCE per contract.
  //
  // This used to re-`reset` on every `existingContract` identity change. The
  // query refetches on mount and on window focus, and each refetch produces a new
  // object (even when the data is identical), so a refetch landing mid-edit threw
  // away whatever the user had typed and re-applied the server's values — and it
  // raced the rate-seeding effect in `HourlyPackage`. Keying the hydration on the
  // contract id keeps the server as the source of truth for the initial values
  // without letting a background refetch stomp an in-progress edit.
  const hydratedContractIdRef = React.useRef<number | null>(null);
  React.useEffect(() => {
    if (!existingContract) return;
    if (hydratedContractIdRef.current === existingContract.id) return;
    hydratedContractIdRef.current = existingContract.id;
    setContractStatus(existingContract.status);
    form.reset(formValues as ContractFormValues);
    setIsFormReady(true);
  }, [existingContract, formValues, form]);

  // Track the previous type to only reset when the user actually changes it
  const prevTypeRef = React.useRef<string | undefined>(undefined);

  // Reset dependent fields when type changes (skip until form is ready)
  React.useEffect(() => {
    if (!isFormReady) return;

    if (prevTypeRef.current === undefined) {
      prevTypeRef.current = selectedType;
      return;
    }

    if (prevTypeRef.current === selectedType) return;
    prevTypeRef.current = selectedType;

    if (selectedType !== 'FIXED') {
      form.resetField('price');
      form.resetField('advancePaymentUnit');
    }
    if (selectedType !== 'HOURLY') {
      form.resetField('hourlyPackageId');
      form.resetField('periodId');
    }
    if (selectedType !== 'SUCCESS_FEE') {
      form.resetField('claimedPrice');
    }
  }, [selectedType, form, isFormReady]);

  // Convert the hourly package's money fields (price, overtime, per-role rates)
  // when the contract currency changes, using dolarRate (USD→UZS ×rate,
  // UZS→USD ÷rate). Hours are not money. Only fires on a real user-driven change.
  const prevCurrencyRef = React.useRef<string | undefined>(undefined);
  React.useEffect(() => {
    if (!isFormReady) return;
    const prev = prevCurrencyRef.current;
    prevCurrencyRef.current = selectedCurrency;
    if (!prev || prev === selectedCurrency) return;
    const dolarRate = form.getValues('dolarRate');
    if (!dolarRate) return;
    const factor =
      selectedCurrency === 'UZS' && prev === 'USD'
        ? dolarRate
        : selectedCurrency === 'USD' && prev === 'UZS'
          ? 1 / dolarRate
          : 1;
    if (factor === 1) return;
    const conv = (v?: number | null) =>
      v == null ? undefined : Math.round(v * factor);
    const hp = form.getValues('hourlyPackage');
    if (hp) {
      form.setValue('hourlyPackage.price', conv(hp.price));
      form.setValue('hourlyPackage.overTimeHourlyCost', conv(hp.overTimeHourlyCost));
      form.setValue(
        'hourlyPackage.rates',
        (hp.rates ?? []).map((r) => ({ roleId: r.roleId, rate: conv(r.rate) ?? 0 }))
      );
    }
  }, [selectedCurrency, isFormReady, form]);

  // Keep the hourly advance payment (and the minimum it is validated against) in
  // lockstep with the tariff price. The advance-payment input renders the tariff
  // price for HOURLY, so the stored value must be written here rather than left to
  // the number input's value-prop echo: that echo made the two drift apart (the
  // field showed the price while form state kept a stale/typed value, which then
  // got saved or tripped `advancePaymentMinimum` against a field that looked right).
  const watchedHourlyPrice = useWatch({ control: form.control, name: 'hourlyPackage.price' });
  React.useEffect(() => {
    if (selectedType !== 'HOURLY') return;
    form.setValue('minAdvanceForHourly', watchedHourlyPrice ?? undefined);
    form.setValue('advancePayment', watchedHourlyPrice ?? undefined);
  }, [watchedHourlyPrice, selectedType, form]);

  const onSubmit: SubmitHandler<ContractFormValues> = async (values) => {
    // Mirror the backend's name rule, which only validates the format when the
    // name changes — checking it unconditionally would lock out the contracts
    // whose names predate the rule. Catching it here turns a server 409 (raw,
    // untranslated) into an inline, localized message on the field itself.
    const nextName = values.name?.trim();
    if (nextName && nextName !== existingContract?.name && !CONTRACT_NAME_PATTERN.test(nextName)) {
      form.setError('name', { message: 'contractNameFormat' });
      return;
    }

    if (values.type === 'HOURLY') {
      if (!values.billingPeriodType) {
        form.setError('billingPeriodType', { message: 'billingPeriodTypeRequiredForHourly' });
        return;
      }
      // INSTANCE contracts carry no scheduled periods (started manually after
      // creation), so they are exempt from the "at least one period" guard.
      if (
        values.billingPeriodType !== 'INSTANCE' &&
        (!values.billingPeriods || values.billingPeriods.length === 0)
      ) {
        form.setError('billingPeriods', { message: 'billingPeriodsRequiredForHourly' });
        return;
      }
    }

    // eslint_disable-next-line @typescript-eslint/no-explicit-any
    const rawPayload: Record<string, any> = {
      ...values,
      description: values.description?.trim() ?? '',
      name: values.name,
      contactFace: values.contactFace?.trim() ?? '',
      contactPhone: values.contactPhone?.trim() ?? '',
      contactEmail: values.contactEmail?.trim() ?? '',
      startsAt: values.startsAt instanceof Date ? toLocalDateStart(values.startsAt) : null,
      endsAt: values.endsAt instanceof Date ? toLocalDateEnd(values.endsAt) : null,
      claimedPrice: values.claimedPrice ?? undefined,
      periodType: values.type === 'HOURLY' ? BILLING_PERIOD_TYPE_MAP[values.billingPeriodType as keyof typeof BILLING_PERIOD_TYPE_MAP] : undefined,
      // Send only each period's endDate. The backend derives the start chain
      // (first period starts at contract.startsAt, every subsequent period
      // starts where the previous one ended), so we no longer compute or
      // transmit per-period start dates. Periods without a concrete end date
      // are dropped (an open-ended INSTANCE contract has a single end-less
      // period the backend ignores anyway; `endDate` is required by the DTO).
      // Sort by endsAt so the array reaches the backend in chronological order.
      billingPeriods: (() => {
        if (values.type !== 'HOURLY') return undefined;
        const withEnds = (values.billingPeriods ?? []).filter(
          (bp) => bp?.endsAt instanceof Date,
        );
        if (withEnds.length === 0) return undefined;
        return withEnds
          .sort((a, b) => (a.endsAt as Date).getTime() - (b.endsAt as Date).getTime())
          .map((bp) => ({ endDate: toLocalDateEnd(bp.endsAt as Date) }));
      })(),
    };

    const payload = removeEmptyFields(rawPayload);

    delete payload['minAdvanceForHourly'];
    delete payload['billingPeriodType'];
    delete payload['billingPeriodCount'];
    // The global dropdown only picks a TEMPLATE — a contract always sends (and the
    // backend clones) its OWN package, never a global package id.
    delete payload['hourlyPackageId'];

    if (values.type === 'HOURLY') {
      // Always send the contract's own hourly package, incl. per-role rates.
      payload['hourlyPackage'] = {
        hours: values.hourlyPackage?.hours,
        price: values.hourlyPackage?.price,
        overTimeHourlyCost: values.hourlyPackage?.overTimeHourlyCost,
        rates: (values.hourlyPackage?.rates ?? [])
          .filter((r) => r.roleId)
          .map((r) => ({ roleId: r.roleId, rate: r.rate ?? 0 })),
      };
    } else {
      delete payload['hourlyPackage'];
    }

    if (values.type === 'SUCCESS_FEE') {
      // Success overrides are optional — omit them so the backend derives and
      // clones the tier from claimedPrice unless the user entered values.
      const sp = values.successPackage;
      const hasOverride =
        !!sp && (sp.advancePayment != null || sp.successPayment != null);
      if (hasOverride) {
        payload['successPackage'] = {
          advancePayment: sp!.advancePayment,
          successPayment: sp!.successPayment,
        };
      } else {
        delete payload['successPackage'];
      }
    } else {
      delete payload['successPackage'];
    }

    if (values.type !== 'FIXED' && values.type !== 'HOURLY' && values.type !== 'SUCCESS_FEE') {
      delete payload['advancePayment'];
    }

    if (isEditMode && id) {
      await updateMutation.mutateAsync({ id, data: payload as CreateContractDto });
      navigate('/app/contracts');
    } else {
      await createMutation.mutateAsync(payload as CreateContractDto);
      clearDraft();
      navigate('/app/contracts');
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  // A contract stays fully editable until it is CLOSED; only a closed contract
  // freezes its fields (backend mirrors this — commercial fields are stripped
  // on update only when status === CLOSED).
  const isFieldDisabled = isEditMode && contractStatus === 'CLOSED';
  // The INSTANCE cycle is always selectable (user request 2026-07-15). Note:
  // switching an existing scheduled contract to INSTANCE hard-deletes its
  // billing periods on the backend ("starts clean").
  const showInstanceOption = true;

  return {
    form,
    onSubmit,
    navigate,
    isEditMode,
    isLoadingContract,
    isFormReady,
    isSubmitting,
    isFieldDisabled,
    showInstanceOption,
    selectedType,
    selectedCurrency,
    isBasicInfoComplete,
    isPricingComplete,
    isFinancialComplete,
    isBillingPeriodsComplete,
    clientIdFromUrl,
  };
}
